/**
 * Tooltips of our own.
 *
 * The browser's are slow, unstyled, and cannot say more than one line, which is
 * no use for an item whose whole point is a column of numbers. These come up
 * after however long somebody has asked for, follow the pointer, and hold as
 * much as the thing being pointed at deserves: a name for a log, a set of
 * bonuses for a blade, what it puts back for a fish.
 */

import { useEffect, useState } from 'react';

/** How far from the pointer it sits, so it never covers what it describes. */
const AWAY = 16;

export interface TooltipContent {
  title: string;
  /**
   * What a plain click on it would do, shown beside the name.
   *
   * The same word the menu puts at the top of its list, so what the tooltip
   * promises and what a click delivers are the same thing. Left out for
   * anything a click does nothing to.
   */
  action?: string;
  /** Lines under the title: what it does, what it needs, what it is worth. */
  lines?: string[];
  /** Numbers shown in two columns, for anything with bonuses. */
  stats?: { label: string; value: string }[];
  /** The last line, quieter than the rest. */
  note?: string;
}

export function Tooltip({
  content,
  delay,
}: {
  /** What to say, or null for nothing under the pointer worth saying anything about. */
  content: TooltipContent | null;
  delay: number;
}): JSX.Element | null {
  const [showing, setShowing] = useState(false);
  const [at, setAt] = useState({ x: 0, y: 0 });

  // The pointer is followed while there is something to say, and only then: a
  // listener that runs on every mouse move for nothing costs a frame for nothing.
  // The wait is a timer rather than a piece of state so nothing is set while the
  // effect is still running.
  useEffect(() => {
    if (!content) return;

    // Once a frame at most. A pointer reports itself as often as the mouse can
    // manage, which on a fast machine is more than twice a frame, and setting
    // state on every one of those is redrawing this two hundred times a second
    // to move it by a pixel.
    let waiting = 0;
    let latest = { x: 0, y: 0 };

    const move = (event: PointerEvent): void => {
      latest = { x: event.clientX, y: event.clientY };
      if (waiting !== 0) return;

      waiting = window.requestAnimationFrame(() => {
        waiting = 0;
        setAt(latest);
      });
    };

    window.addEventListener('pointermove', move);

    const timer = window.setTimeout(() => setShowing(true), Math.max(0, delay));

    return () => {
      window.removeEventListener('pointermove', move);
      window.clearTimeout(timer);
      if (waiting !== 0) window.cancelAnimationFrame(waiting);
      setShowing(false);
    };
  }, [content, delay]);

  if (!content || !showing) return null;

  // Kept on screen: near the right edge it flips to the other side of the
  // pointer, and near the bottom it sits above it.
  const flipX = at.x > window.innerWidth - 240;
  const flipY = at.y > window.innerHeight - 200;

  const style = {
    left: flipX ? undefined : at.x + AWAY,
    right: flipX ? window.innerWidth - at.x + AWAY : undefined,
    top: flipY ? undefined : at.y + AWAY,
    bottom: flipY ? window.innerHeight - at.y + AWAY : undefined,
  };

  return (
    <div className="tooltip" style={style} role="tooltip">
      <strong>
        {content.title}
        {content.action ? <em>{content.action}</em> : null}
      </strong>

      {(content.lines ?? []).map((line) => (
        <span key={line}>{line}</span>
      ))}

      {content.stats && content.stats.length > 0 ? (
        <div className="tooltip-stats">
          {content.stats.map((stat) => (
            <div key={stat.label}>
              <span>{stat.label}</span>
              <span>{stat.value}</span>
            </div>
          ))}
        </div>
      ) : null}

      {content.note ? <small>{content.note}</small> : null}
    </div>
  );
}
